import { Event as TEvent } from "../../../../Global/Base/Events";
import { EnumType } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { ChartType } from "../../../UI.Controls/ChartElement";
import { IChartColumnDefinition } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ChartColumnDefinition } from "../XGControls/Frames/ChartSettingsManager";
import { GenericListFrame } from "../XGControls/Frames/GenericListFrame";
import { ChartItemRow } from "./ChartItemRow";
import { Control } from "./Control";
import { ControlParams } from "./ControlInterfaces";

/**
 * Parametry konstruktoru tridy {@link ChartVerticalAxis}.
 */
export type ChartVerticalAxisParams = ControlParams & {
    masterFrame: GenericListFrame;
    datasets: IChartColumnDefinition[];
    comboBoxData: EnumType;
    chartType: ChartType;
};

/**
 * třída představuje sekci Svislá osa v ChartSettingsPanel => ChartSettingsContentBuilder
 */
export class ChartVerticalAxis extends Control {
    /** událost změny agregační funkce */
    public aggregationFunctionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny názvu kategorie */
    public captionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny atributu osy y */
    public attrNameChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost kliku na tlačítko smazat */
    public deleteDatasetClicked: TEvent<string[], void> = new TEvent<string[], void>();
    private _listenerClick: EventListener;
    /** představuje řádek svislé osy, který obsahuje + Kategorie */
    private _newCategoryElement: HTMLElement;
    /** představuje část řádku svislé osy - Kategorie */
    private _newCategoryRow: HTMLElement;
    /** představuje část řádku svislé osy - + */
    private _newCategorySign: HTMLElement;
    /** obsahuje pocet atributu svisle osy */
    private _categoryNum: number;
    private _chartType: ChartType;
    private _masterFrame: GenericListFrame;
    private _datasets: IChartColumnDefinition[];
    private _comboBoxData: EnumType;
    /** obsahuje záznamy svislé osy spolu s _lastElement */
    private _chartItem: HTMLElement;
    /** pole záznamů svislé osy */
    private _itemArray: ChartItemRow[] = [];
    /** hlavní css název třídy */
    private _cssClass: string = "chart-vertical-axis";

    constructor(params: ChartVerticalAxisParams) {
        super(params);
        const { masterFrame, datasets, comboBoxData, chartType } = params;
        this.element.classList.add(this._cssClass);
        this._masterFrame = masterFrame;
        this._datasets = datasets;
        this._comboBoxData = comboBoxData;
        this._chartType = chartType;

        this._initialize();
        this._setListeners();
        this._categoryNum = 0;
        this._recreateItems();
    }

    private _initialize(): void {
        const verticalAxisText = createElement({ classes: [this._cssClass + "__header"] });
        Localization.addLocTxtData(verticalAxisText, "GUI-676795"); // svislá osa
        this._chartItem = createElement();
        this._chartItem.classList.add(this._cssClass + "__block");

        this._newCategoryRow = createElement({ classes: [this._cssClass + "__row"] });
        const rightPart = createElement({ classes: [this._cssClass + "__cell"] });
        this._newCategorySign = createElement({ type: "button", classes: [this._cssClass + "__add"], html: "+" });
        this._newCategoryElement = createElement({ type: "button", classes: [this._cssClass + "__item"] });
        Localization.addLocData(this._newCategoryElement, "GUI-232972"); // kategorie
        rightPart.appendChild(this._newCategorySign);
        rightPart.appendChild(this._newCategoryElement);
        this._newCategoryRow.appendChild(rightPart);
        this._chartItem.appendChild(this._newCategoryRow);
        this.element.appendChild(verticalAxisText);
        this.element.appendChild(this._chartItem);
    }

    private _setListeners(): void {
        this._newCategoryElement.addEventListener("click", this._listenerClick = (e: Event) => {
            this._addNewItem(e);
        });
        this._newCategorySign.addEventListener("click", this._listenerClick);
    }

    /**
     * pokud v chartsettinsmanageru existují datasety, vytvoří se jejich grafické zobrazení
     */
    private _recreateItems(): void {
        if (this._datasets && this._datasets.length > 0) {
            for (const dataset of this._datasets) {
                const newItem = new ChartItemRow(this._comboBoxData, dataset, this._masterFrame, this._chartType);
                this._setChartSettingsManagerEvents(newItem);
                newItem.initialize();
                this._chartItem.appendChild(newItem.element);
                this._itemArray.push(newItem);
                // categorynum by mělo být alespoň o jedno vyšší než je id libovolného datasetu
                if (this._categoryNum <= +dataset.id)
                    this._categoryNum = 1 + (+dataset.id);
            }
        }
    }

    /**
     * Přidá záznam svislé osy před _lastRow.
     * Přitom otevře dropdownmenu.
     * Dropdownmenu je nejdříve otevíráno u _lastrow (+ kategorie) a na místo _lastrow se pak dá row (v insertAdjacentElement).
     * To je proto, že když se dávalo dropdownmenu k row (kam patří), tak se dropdownmenu nezobrazilo na pozici kde má (pod row),
     * ale zobrazovalo se uprostřed row, asi díky probíhajícímu vykreslování.
     * @param openDropDownMenuEvent Element, na základě kterého se vytváří ChartItemRow
     */
    private _addNewItem(openDropDownMenuEvent: Event): void {
        const dataset = new ChartColumnDefinition();
        dataset.id = this._categoryNum + "";
        this._datasets.push(dataset);
        const row = new ChartItemRow(this._comboBoxData, dataset, this._masterFrame, this._chartType);
        this._setChartSettingsManagerEvents(row);
        row.initialize();
        this._chartItem.appendChild(row.element);
        row.openDropDownMenu(row.item, openDropDownMenuEvent);
        this._categoryNum++;
        this._itemArray.push(row);
    }

    private _setChartSettingsManagerEvents(chartItemRow: ChartItemRow): void {
        chartItemRow.aggregationFunctionChanged.add(value => this._runChartSettingsManagerEvents(value, this.aggregationFunctionChanged));
        chartItemRow.captionChanged.add(value => this._runChartSettingsManagerEvents(value, this.captionChanged));
        chartItemRow.attrNameChanged.add(value => this._runChartSettingsManagerEvents(value, this.attrNameChanged));
        chartItemRow.deleteDatasetClicked.add(value => this._runChartSettingsManagerEvents(value, this.deleteDatasetClicked));
    }

    private _runChartSettingsManagerEvents(value: string[], chartSettingsManagerEvent: TEvent<string[], void>): void {
        chartSettingsManagerEvent.trigger(value, null);
    }

    /**
    * Provede nastavení řádku na základě typu grafu
    * @param chartType Typ grafu
    */
    public setChartType(chartType: ChartType): void {
        this._chartType = chartType;
        this._itemArray.forEach(item => item.chartType = chartType);
    }

    public override dispose(): void {
        this._newCategoryElement.removeEventListener("click", this._listenerClick);
        this._newCategorySign.removeEventListener("click", this._listenerClick);
        if (this._itemArray) {
            for (const element of this._itemArray) {
                if (element && !element.isDisposed) {
                    element.dispose();
                }
            }
        }

        super.dispose();
    }
}
